import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, Paper, CircularProgress, Alert } from '@mui/material';

const TestDatabaseStore = () => {
  const [testName, setTestName] = useState(`Test Meal ${new Date().toLocaleTimeString()}`);
  const [testDesc, setTestDesc] = useState('Test description for database storage');
  const [loading, setLoading] = useState(false);
  const [dbStatus, setDbStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Check database connection status
  useEffect(() => {
    const checkDbStatus = async () => {
      try {
        const response = await fetch('http://192.168.168.68:5000/api/test/db-status');
        const data = await response.json();
        setDbStatus(data);
      } catch (err) {
        console.error('Error checking DB status:', err);
        setDbStatus({ status: 'error', connected: false, error: err.message });
      }
    };

    checkDbStatus();
  }, []);

  // Handler for direct save test
  const handleTestSave = async () => {
    try {
      setLoading(true);
      setError(null);
      setResult(null);

      // Make direct API call to test endpoint
      const response = await fetch('http://192.168.168.68:5000/api/test/create-meal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: testName,
          description: testDesc
        })
      });

      const data = await response.json();
      setResult(data);
      console.log('Test save result:', data);
    } catch (err) {
      console.error('Test save error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handler for direct admin meal save
  const handleDirectAdminSave = async () => {
    try {
      setLoading(true);
      setError(null);
      setResult(null);

      // Get auth token
      const token = localStorage.getItem('token');
      
      // Make direct API call to admin endpoint
      const response = await fetch('http://192.168.168.68:5000/api/admin/meals', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: testName,
          description: testDesc,
          mealType: 'dinner',
          dietaryType: ['vegetarian'],
          cuisineStyle: ['healthy'],
          seasonality: ['all']
        })
      });

      // Try to get the response data
      let data;
      try {
        data = await response.json();
      } catch (e) {
        data = await response.text();
      }

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${typeof data === 'string' ? data : JSON.stringify(data)}`);
      }

      setResult({
        success: true,
        message: 'Direct admin save successful',
        data
      });
      console.log('Direct admin save result:', data);
    } catch (err) {
      console.error('Direct admin save error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3, maxWidth: 800, mx: 'auto', my: 4 }}>
      <Typography variant="h5" gutterBottom>Database Storage Test Tool</Typography>
      
      {/* DB Status */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6">Database Status:</Typography>
        {dbStatus ? (
          <Alert severity={dbStatus.connected ? 'success' : 'error'}>
            Status: {dbStatus.status} | Connected: {dbStatus.connected ? 'Yes' : 'No'}
          </Alert>
        ) : (
          <CircularProgress size={20} />
        )}
      </Box>

      {/* Test Form */}
      <Box component="form" sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Test Meal Name"
          value={testName}
          onChange={(e) => setTestName(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Test Description"
          value={testDesc}
          onChange={(e) => setTestDesc(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            onClick={handleTestSave} 
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Test Basic Save'}
          </Button>
          <Button 
            variant="contained" 
            color="secondary"
            onClick={handleDirectAdminSave} 
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Test Admin Save'}
          </Button>
        </Box>
      </Box>

      {/* Results */}
      {result && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="subtitle1">{result.message || 'Success!'}</Typography>
          <pre style={{ maxHeight: '200px', overflow: 'auto', fontSize: '0.85rem' }}>
            {JSON.stringify(result, null, 2)}
          </pre>
        </Alert>
      )}

      {/* Errors */}
      {error && (
        <Alert severity="error">
          <Typography variant="subtitle1">Error:</Typography>
          <Typography>{error}</Typography>
        </Alert>
      )}
    </Paper>
  );
};

export default TestDatabaseStore;
